import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface DashboardAnalytics {
  overview: {
    totalMunduses: number;
    activeMunduses: number;
    totalMetrics: number;
    totalAgents: number;
    totalReports: number;
  };
  scoreEvolution: Array<{
    date: string;
    averageScore: number;
  }>;
  recentActivity: Array<{
    id: string;
    metricName: string;
    mundusName: string;
    mundusId: string;
    oldValue: number;
    newValue: number;
    change: number;
    timestamp: string;
  }>;
  munduses: Array<{
    id: string;
    name: string;
    goalType: string;
    isActive: boolean;
    metricsCount: number;
    agentsCount: number;
    reportsCount: number;
    latestScore: number;
  }>;
}

export interface MundusAnalytics {
  mundus: {
    id: string;
    name: string;
    description: string | null;
    goalType: string;
    isActive: boolean;
  };
  overview: {
    metricsCount: number;
    agentsCount: number;
    reportsCount: number;
    latestScore: number;
  };
  spiderData: Array<{
    metric: string;
    baseline: number;
    current: number;
    target: number;
    fullMark: number;
  }>;
  metricEvolution: Array<{
    metricId: string;
    metricName: string;
    unit: string;
    targetValue: number;
    currentValue: number;
    progress: number;
    evolution: Array<{
      date: string;
      value: number;
    }>;
  }>;
  reportScores: Array<{
    date: string;
    score: number;
  }>;
  agentActivity: Array<{
    id: string;
    name: string;
    type: string;
    isActive: boolean;
    executionCount: number;
    lastExecutedAt: string | null;
  }>;
}

export interface MetricEvolution {
  metric: {
    id: string;
    name: string;
    unit: string;
    currentValue: number;
    targetValue: number;
    mundus: {
      id: string;
      name: string;
    };
  };
  evolution: Array<{
    date: string;
    value: number;
    min: number;
    max: number;
  }>;
  stats: {
    totalUpdates: number;
    averageValue: number;
    trend: number;
  };
}

export interface MundusComparison {
  mundusId: string;
  mundusName: string;
  goalType: string;
  metrics: Array<{
    name: string;
    progress: number;
  }>;
  overallProgress: number;
  latestScore: number;
}

/**
 * Get dashboard analytics overview
 */
export const getDashboardAnalytics = async (timeRange: string = '30d'): Promise<DashboardAnalytics> => {
  const response = await api.get('/analytics/dashboard', {
    params: { timeRange },
  });
  return response.data.data;
};

/**
 * Get Mundus-specific analytics
 */
export const getMundusAnalytics = async (mundusId: string, timeRange: string = '30d'): Promise<MundusAnalytics> => {
  try {
    console.log('Fetching analytics for mundus:', mundusId, 'with timeRange:', timeRange);
    const response = await api.get(`/analytics/mundus/${mundusId}`, {
      params: { timeRange },
    });
    console.log('Analytics response:', response.data);
    return response.data.data;
  } catch (error: any) {
    console.error('Analytics API error:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    throw error;
  }
};

/**
 * Get metric evolution over time
 */
export const getMetricEvolution = async (
  metricId: string,
  timeRange: string = '30d',
  granularity: string = 'day'
): Promise<MetricEvolution> => {
  const response = await api.get(`/analytics/metric/${metricId}/evolution`, {
    params: { timeRange, granularity },
  });
  return response.data.data;
};

/**
 * Get all Mundus comparison data
 */
export const getMundusComparison = async (): Promise<MundusComparison[]> => {
  const response = await api.get('/analytics/comparison');
  return response.data.data;
};
